/**
 * 공통 API 인터셉터
 * 전역 토큰 갱신 상태 관리 및 동시성 제어
 */
import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import authStateManager from './authStateManager';
import RetryManager from './retryManager';

export interface ApiErrorData {
    message: string;
    status?: number;
    code?: string;
    isTokenExpired?: boolean;
    isNetworkError?: boolean;
}

/**
 * API 에러 클래스
 */
export class ApiError extends Error {
    public status?: number;
    public code?: string;
    public isTokenExpired?: boolean;
    public isNetworkError?: boolean;

    constructor(error: ApiErrorData) {
        super(error.message);
        this.name = 'ApiError';
        this.status = error.status;
        this.code = error.code;
        this.isTokenExpired = error.isTokenExpired;
        this.isNetworkError = error.isNetworkError;
    }
}

/**
 * 공통 인터셉터 설정 함수
 */
export function setupApiInterceptors(apiInstance: AxiosInstance): void {
    // 요청 인터셉터 - 토큰 자동 추가
    apiInstance.interceptors.request.use(
        async (config: InternalAxiosRequestConfig) => {
            try {
                // 유효한 토큰 가져오기 (갱신 포함)
                const token = await authStateManager.getValidToken();
                if (token) {
                    config.headers = config.headers || {};
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (error) {
                console.warn('토큰 가져오기 실패:', error);
                // 토큰이 없어도 요청은 진행 (로그인 API 등)
            }

            return config;
        },
        (error) => {
            return Promise.reject(new ApiError({
                message: '요청 설정 중 오류가 발생했습니다.',
                code: 'REQUEST_ERROR'
            }));
        }
    );

    // 응답 인터셉터 - 토큰 만료 시 자동 갱신
    apiInstance.interceptors.response.use(
        (response: AxiosResponse) => {
            return response;
        },
        async (error) => {
            const originalRequest = error.config;

            // 401 에러이고 아직 재시도하지 않은 경우
            if (error.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;

                try {
                    // 전역 상태 관리자를 통한 토큰 갱신
                    const newToken = await authStateManager.getValidToken();

                    // 원래 요청에 새 토큰 적용
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return apiInstance(originalRequest);

                } catch (refreshError: any) {
                    console.error('토큰 갱신 실패:', refreshError);

                    // 갱신 실패 시 AuthService를 통한 일관된 로그아웃 처리
                    // AuthService.logout()이 토큰 정리와 리다이렉트를 모두 처리함
                    const { AuthService } = await import('../services/AuthService');
                    AuthService.logout();

                    return Promise.reject(new ApiError({
                        message: '인증이 만료되었습니다. 다시 로그인해주세요.',
                        status: 401,
                        code: 'TOKEN_REFRESH_FAILED',
                        isTokenExpired: true
                    }));
                }
            }

            // 네트워크 오류나 서버 오류의 경우 재시도 로직 적용
            if (RetryManager.isRetryableError(error) && !originalRequest._retry) {
                originalRequest._retry = true;

                const retryResult = await RetryManager.retryApiCall(async () => {
                    return apiInstance(originalRequest);
                });

                if (retryResult.success) {
                    return retryResult.result;
                } else {
                    // 재시도 실패 시 원래 에러 반환
                    return Promise.reject(retryResult.error);
                }
            }

            // 401이 아니거나 이미 재시도한 경우
            const apiError: ApiErrorData = {
                message: error.response?.data?.message || error.message || '알 수 없는 오류가 발생했습니다.',
                status: error.response?.status,
                code: error.code,
                isNetworkError: !error.response
            };

            return Promise.reject(new ApiError(apiError));
        }
    );
}

/**
 * 에러 메시지 변환 함수
 */
export function getErrorMessage(error: any): string {
    if (error instanceof ApiError) {
        return error.message;
    }

    if (error.response?.data?.message) {
        return error.response.data.message;
    }

    if (error.message) {
        return error.message;
    }

    return '알 수 없는 오류가 발생했습니다.';
}

/**
 * 에러 타입 확인 함수
 */
export function isTokenExpiredError(error: any): boolean {
    return error instanceof ApiError && (error.isTokenExpired === true);
}

export function isNetworkError(error: any): boolean {
    return error instanceof ApiError && (error.isNetworkError === true);
}

const apiInterceptorUtils = {
    setupApiInterceptors,
    getErrorMessage,
    isTokenExpiredError,
    isNetworkError,
    ApiError
};

export default apiInterceptorUtils;
